/**
 * Colours for a Plan card's GitHub board-status pill.
 *
 * A Projects v2 board column carries a colour chosen from a fixed enum rather than a hex value, so
 * the pill has to map that enum itself. The hexes below are picked to read on both the Plan
 * column's panel background and the Timeline canvas, and deliberately avoid the MoSCoW palette in
 * canvasUtils.tsx — a status pill sitting next to a priority pill must not look like one.
 *
 * Lives here rather than in canvasUtils.tsx, which is SVG-canvas geometry and node styling; this
 * is DOM pill styling for an integration.
 */
import { ExternalItemLink } from 'types';

/** GitHub's ProjectV2SingleSelectFieldOption colour enum. */
const OPTION_COLORS: Record<string, string> = {
    GRAY: '#6B7280',
    BLUE: '#3B82F6',
    GREEN: '#22C55E',
    YELLOW: '#EAB308',
    ORANGE: '#F97316',
    RED: '#EF4444',
    PINK: '#EC4899',
    PURPLE: '#A855F7',
};

/** Used when a board column has no colour, and for the open/closed fallback. */
const FALLBACK_COLOR = OPTION_COLORS.GRAY;
const OPEN_COLOR = OPTION_COLORS.GREEN;
const CLOSED_COLOR = OPTION_COLORS.PURPLE;

export function boardStatusColor(color?: string | null): string {
    if (!color) return FALLBACK_COLOR;
    return OPTION_COLORS[String(color).toUpperCase()] || FALLBACK_COLOR;
}

/** What the status pill shows, or null when the card has nothing worth showing. */
export interface StatusPillContent {
    label: string;
    color: string;
    /** Longer form for the pill's tooltip — names the board when there is one. */
    title: string;
}

/**
 * Derives the pill from a card's link.
 *
 * The board column is preferred; an issue that is on no board falls back to its own open/closed
 * state so a synced card always says something, rather than showing a pill only for the subset of
 * issues someone has already dragged onto a board.
 */
export function statusPillContent(link?: ExternalItemLink | null): StatusPillContent | null {
    if (!link) return null;

    if (link.boardStatus && link.boardStatus.name) {
        const { name, color, projectTitle } = link.boardStatus;
        return {
            label: name,
            color: boardStatusColor(color),
            title: projectTitle ? `${projectTitle} — ${name}` : name,
        };
    }

    if (link.issueState === 'open' || link.issueState === 'closed') {
        const label = link.issueState === 'open' ? 'Open' : 'Closed';
        return {
            label,
            color: link.issueState === 'open' ? OPEN_COLOR : CLOSED_COLOR,
            title: `Issue ${label.toLowerCase()} — not on a project board`,
        };
    }

    return null;
}
